import axios from "axios";
import { auth, getAppCheckToken } from "@/service/firebase";
import { managementApiBaseUrl } from "@/service/totemDeviceConfig";
import { eventRequestId } from "@/service/eventAttendance";

export const QUOTA_PROTOCOL = "EVENT_PHOTO_QUOTA_V1";
const PREFIX = "photonow.event-photo-quota.v1:";
const MAX_RESERVATIONS = 50;
const MAX_BYTES = 512 * 1024;
function quotaClient() {
  // The legacy global interceptor rejects error.response instead of AxiosError.
  // Keep HTTP refusals distinct from offline completion on a dedicated client.
  const client = axios.create({ baseURL: managementApiBaseUrl(), timeout: 15000 });
  // main.js sets this response-only header for the local PrintServer. Sending
  // it to the remote API would fail its CORS preflight. Preserve legacy defaults.
  if (client.defaults?.headers?.post) delete client.defaults.headers.post["Access-Control-Allow-Origin"];
  return client;
}
const uuid = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value || "");
function context(id) {
  const user = auth?.currentUser;
  const project = auth?.app?.options?.projectId;
  if (!user?.uid || !project || !/^[A-Za-z0-9_-]{1,128}$/.test(id || "")) throw new Error("Sessão da cota Evento ausente.");
  return { id, uid: user.uid, project, key: `${PREFIX}${project}:${user.uid}:${id}` };
}
function assertContext(scope) {
  const current = context(scope.id);
  if (current.uid !== scope.uid || current.project !== scope.project) throw new Error("Sessão alterada durante a cota Evento.");
}
function load(scope) {
  assertContext(scope);
  const raw = localStorage.getItem(scope.key);
  if (raw) {
    if (raw.length > MAX_BYTES) throw new Error("Armazenamento da cota excedeu o limite. Peça ajuda ao ADMIN.");
    let state;
    try { state = JSON.parse(raw); } catch (_) { throw new Error("Registro local da cota inválido. Peça ajuda ao ADMIN."); }
    if (state.project !== scope.project || state.uid !== scope.uid || state.totemId !== scope.id || !uuid(state.instanceId)
      || !state.reservations || typeof state.reservations !== "object" || Array.isArray(state.reservations)
      || Object.keys(state.reservations).length > MAX_RESERVATIONS) throw new Error("Registro local da cota inválido. Peça ajuda ao ADMIN.");
    return state;
  }
  const state = { project: scope.project, uid: scope.uid, totemId: scope.id, instanceId: eventRequestId(), configured: false, reservations: {} };
  save(scope, state);
  return state;
}
function save(scope, state) {
  assertContext(scope);
  const serialized = JSON.stringify(state);
  if (serialized.length > MAX_BYTES || Object.keys(state.reservations).length > MAX_RESERVATIONS) throw new Error("A cota tem registros pendentes demais. Solicite conferência do ADMIN.");
  localStorage.setItem(scope.key, serialized);
}
async function request(scope, method, suffix, body, token) {
  assertContext(scope);
  const user = auth.currentUser;
  const [idToken, appCheck] = await Promise.all([user.getIdToken(), getAppCheckToken()]);
  assertContext(scope);
  const state = load(scope);
  const response = await quotaClient().request({ method,
    url: `/totem/${encodeURIComponent(scope.id)}/cota-evento${suffix}`, data: body,
    headers: { Authorization: `Bearer ${idToken}`, "X-PhotoNow-Quota-Instance": state.instanceId,
      ...(appCheck ? { "X-Firebase-AppCheck": appCheck } : {}), ...(token ? { "X-PhotoNow-Reservation-Token": token } : {}) } });
  assertContext(scope);
  return response.data;
}
function validConfig(config, id) {
  if (config?.totemId !== id || config.protocolVersion !== QUOTA_PROTOCOL || typeof config.enabled !== "boolean"
    || typeof config.configured !== "boolean" || config.enabled && (!Number.isSafeInteger(config.available) || config.available < 0)) throw new Error("Resposta da cota Evento inválida.");
  return config;
}
export function quotaLocalState(id) { return load(context(id)); }
export function quotaReservation(id, purchaseId) { return load(context(id)).reservations[purchaseId] || null; }
export async function refreshPhotoQuota(id, idle = false) {
  const scope = context(id); const state = load(scope);
  if (typeof navigator?.locks?.request !== "function") {
    const config = validConfig(await request(scope, "get", "/config"), id);
    if (config.configured) throw new Error("Atualize o navegador para usar a cota Evento com segurança.");
    return config; // Do not advertise an unsupported browser as compatible.
  }
  const config = validConfig(await request(scope, "post", "/capability", { instanceId: state.instanceId, protocolVersion: QUOTA_PROTOCOL, idle }), id);
  const current = load(scope);
  save(scope, { ...current, configured: config.configured, config });
  await flushQuotaAcceptances(id);
  return config;
}
function assertManifest(manifest, id, purchaseId) {
  if (!manifest || manifest.compraId !== purchaseId || manifest.totemId !== id || !Array.isArray(manifest.saidas) || !manifest.saidas.length) throw new Error("Composição da cota não corresponde ao atendimento.");
}
export async function reservePhotoQuota(id, manifest) {
  const scope = context(id); const purchaseId = manifest?.compraId;
  assertManifest(manifest, id, purchaseId);
  let state = load(scope); const existing = state.reservations[purchaseId];
  if (existing) {
    if (JSON.stringify(existing.manifest) !== JSON.stringify(manifest)) throw new Error("A composição mudou depois da reserva. Solicite conferência antes de continuar.");
    if (existing.status === "RESERVED") return existing;
    if (existing.status !== "PENDING") throw new Error("Este atendimento já iniciou a impressão ou precisa de conferência. Não envie novamente.");
  } else {
    const config = await refreshPhotoQuota(id, false);
    if (!config.enabled) return null;
    state = load(scope);
    // Discard only old acknowledged terminal records; never drop pending evidence.
    const reservations = Object.fromEntries(Object.entries(state.reservations).filter(([, value]) => !["SYNCED", "RELEASED"].includes(value.status)));
    if (Object.keys(reservations).length >= MAX_RESERVATIONS) throw new Error("Conferência da cota pendente. Procure o ADMIN.");
    reservations[purchaseId] = { purchaseId, requestId: eventRequestId(), manifest, status: "PENDING" };
    save(scope, { ...state, reservations });
  }
  const saved = load(scope).reservations[purchaseId];
  const result = await request(scope, "post", "/reservas", { instanceId: load(scope).instanceId,
    requestId: saved.requestId, purchaseId, manifest: saved.manifest });
  if (result.purchaseId !== purchaseId || !["RESERVED", "CONSUMED", "RELEASED"].includes(result.status) || !/^[a-f0-9]{64}$/.test(result.reservationToken || "")
    || result.sheets !== manifest.saidas.length) throw new Error("A reserva precisa de conferência antes de continuar.");
  const current = load(scope); const reservation = { ...saved, ...result, status: result.status === "CONSUMED" ? "SYNCED" : result.status };
  save(scope, { ...current, reservations: { ...current.reservations, [purchaseId]: reservation } });
  return reservation;
}
function operation(scope, reservation, requestId) { return { instanceId: load(scope).instanceId, requestId, reservationToken: reservation.reservationToken }; }
export async function startQuotaPayment(id, purchaseId) {
  const scope = context(id); const state = load(scope); const saved = state.reservations[purchaseId];
  if (!saved) {
    if (state.configured) throw new Error("Pagamento sem reserva de cota. Solicite conferência.");
    return;
  }
  if (saved.status !== "RESERVED") throw new Error("Este atendimento não pode iniciar outro pagamento.");
  if (saved.paymentStartedAt) return;
  const pending = { ...saved, paymentRequestId: saved.paymentRequestId || eventRequestId() };
  save(scope, { ...state, reservations: { ...state.reservations, [purchaseId]: pending } });
  // No offline bypass here: payment must never open without the server marker.
  const result = await request(scope, "post", `/reservas/${purchaseId}/payment`, operation(scope, pending, pending.paymentRequestId));
  if (result.purchaseId !== purchaseId || result.status !== "RESERVED" || typeof result.paymentStartedAt !== "string"
    || !Number.isFinite(Date.parse(result.paymentStartedAt))) throw new Error("Não foi possível confirmar a reserva antes do pagamento.");
  const current = load(scope);
  save(scope, { ...current, reservations: { ...current.reservations, [purchaseId]: { ...current.reservations[purchaseId], paymentStartedAt: result.paymentStartedAt } } });
}
export async function reserveQuotaBeforePayment(id, manifest) {
  // Sem manifesto o id chega vazio e o erro viraria "Sessão ausente".
  assertManifest(manifest, id, manifest?.compraId);
  const scope = context(id);
  const reservation = await reservePhotoQuota(id, manifest);
  assertContext(scope);
  if (reservation) await startQuotaPayment(id, manifest.compraId);
  assertContext(scope);
  return reservation;
}
async function claimQuotaDispatch(id, purchaseId, sheets) {
  const scope = context(id); let state = load(scope); const saved = state.reservations[purchaseId];
  if (!saved) {
    if (state.configured) throw new Error("Compra sem reserva de cota. Solicite conferência, sem repetir o envio.");
    return null;
  }
  if (saved.status !== "RESERVED" || saved.sheets !== sheets) throw new Error("Impressão já iniciada ou composição divergente. O saldo continua reservado; procure o ADMIN.");
  // Persist the first-send intent BEFORE any local printer POST. A reload or
  // another tab sees DISPATCHING and must not send the non-idempotent legacy job again.
  const dispatch = { ...saved, status: "DISPATCHING", dispatchRequestId: eventRequestId() };
  save(scope, { ...state, reservations: { ...state.reservations, [purchaseId]: dispatch } });
  try {
    await request(scope, "post", `/reservas/${purchaseId}/dispatch`, operation(scope, dispatch, dispatch.dispatchRequestId));
  } catch (error) {
    // Offline completion is allowed only for the locally persisted reservation.
    // An explicit server refusal cannot become an offline bypass.
    if (error?.response) {
      await markQuotaUncertain(id, purchaseId);
      throw error;
    }
  }
  assertContext(scope);
  return dispatch;
}
export async function prepareQuotaDispatch(id, purchaseId, sheets) {
  const scope = context(id);
  if (!load(scope).reservations[purchaseId] && !load(scope).configured) return null;
  if (typeof navigator?.locks?.request !== "function") throw new Error("Atualize o navegador para enviar com cota Evento.");
  return navigator.locks.request(`${scope.key}:dispatch`, { mode: "exclusive" }, () => claimQuotaDispatch(id, purchaseId, sheets));
}
export function isPrintServiceAcceptance(response, { purchaseId }) {
  if (response?.status !== 200) return false;
  if (response.data === "Sucesso") return true;
  const value = response.data;
  return value?.status === "ACCEPTED_BY_PRINT_SERVICE" && value.jobId === purchaseId
    && value.physicallyConfirmed === false;
}
export async function acceptPhotoQuota(id, purchaseId) {
  const scope = context(id); const state = load(scope); const saved = state.reservations[purchaseId];
  if (!saved) return;
  if (["ACCEPTED", "SYNCED"].includes(saved.status)) return flushQuotaAcceptances(id);
  if (saved.status !== "DISPATCHING") throw new Error("Aceite sem despacho da cota.");
  const accepted = { ...saved, status: "ACCEPTED", acceptedRequestId: eventRequestId(), observedAt: new Date().toISOString() };
  // Never make the printer call again when recording/delivery fails.
  save(scope, { ...state, reservations: { ...state.reservations, [purchaseId]: accepted } });
  try { await flushQuotaAcceptances(id); }
  catch (_) { /* Acceptance is durable. Offline delivery never reprints or stops completion. */ }
}
export async function markQuotaUncertain(id, purchaseId) {
  const scope = context(id); const state = load(scope); const saved = state.reservations[purchaseId];
  if (!saved || !["DISPATCHING", "UNCERTAIN"].includes(saved.status)) return;
  const uncertain = { ...saved, status: "UNCERTAIN", uncertainRequestId: saved.uncertainRequestId || eventRequestId() };
  save(scope, { ...state, reservations: { ...state.reservations, [purchaseId]: uncertain } });
  try { await request(scope, "post", `/reservas/${purchaseId}/uncertain`, operation(scope, uncertain, uncertain.uncertainRequestId)); }
  catch (_) { /* Local durable uncertainty remains; no automatic release/reprint. */ }
}
export async function flushQuotaAcceptances(id) {
  const scope = context(id); const state = load(scope);
  for (const saved of Object.values(state.reservations)) {
    if (saved.status !== "ACCEPTED") continue;
    try {
      const result = await request(scope, "post", `/reservas/${saved.purchaseId}/accepted`, operation(scope, saved, saved.acceptedRequestId));
      if (result.purchaseId !== saved.purchaseId || result.status !== "CONSUMED") throw new Error("Aceite pendente de conferência.");
      const current = load(scope);
      save(scope, { ...current, reservations: { ...current.reservations, [saved.purchaseId]: { ...current.reservations[saved.purchaseId], status: "SYNCED" } } });
    } catch (error) {
      if (error?.response?.status === 409) {
        const current = load(scope);
        save(scope, { ...current, reservations: { ...current.reservations, [saved.purchaseId]: { ...current.reservations[saved.purchaseId], status: "UNCERTAIN" } } });
      }
      throw error;
    }
  }
}
export async function reconcileQuotaReservations(id) {
  const scope = context(id); const state = load(scope);
  for (const saved of Object.values(state.reservations)) {
    if (saved.status === "PENDING") {
      await reservePhotoQuota(id, saved.manifest); // Recover the same request after a lost reserve reply.
      continue;
    }
    if (!["RESERVED", "DISPATCHING", "UNCERTAIN"].includes(saved.status) || !saved.reservationToken) continue;
    const result = await request(scope, "get", `/reservas/${saved.purchaseId}`, undefined, saved.reservationToken);
    if (result.purchaseId !== saved.purchaseId) throw new Error("Reserva de outro atendimento.");
    if (["CONSUMED", "RELEASED"].includes(result.status)) {
      const current = load(scope);
      save(scope, { ...current, reservations: { ...current.reservations, [saved.purchaseId]: { ...current.reservations[saved.purchaseId], status: result.status === "CONSUMED" ? "SYNCED" : "RELEASED" } } });
    }
  }
}
export async function releaseUndispatchedQuota(id) {
  const scope = context(id); const state = load(scope);
  for (const saved of Object.values(state.reservations)) {
    if (saved.status !== "RESERVED") continue;
    const requestId = saved.releaseRequestId || eventRequestId();
    const current = load(scope);
    save(scope, { ...current, reservations: { ...current.reservations, [saved.purchaseId]: { ...current.reservations[saved.purchaseId], releaseRequestId: requestId } } });
    try {
      const result = await request(scope, "post", `/reservas/${saved.purchaseId}/release`, operation(scope, saved, requestId));
      if (result.status !== "RELEASED") throw new Error("Reserva ainda não liberada.");
      const latest = load(scope);
      save(scope, { ...latest, reservations: { ...latest.reservations, [saved.purchaseId]: { ...latest.reservations[saved.purchaseId], status: "RELEASED" } } });
    } catch (error) {
      if (error?.response?.status === 409) continue; // A payment/dispatch may exist; ADMIN decides.
      throw error;
    }
  }
}
export async function redeemPhotoQuota(id, code, requestId) {
  const scope = context(id); const state = load(scope);
  const pending = state.pendingTopup || { code, requestId };
  if (pending.code !== code) throw new Error("Confirme primeiro a recarga anterior com o mesmo código.");
  save(scope, { ...state, pendingTopup: pending });
  let result;
  try { result = validConfig(await request(scope, "post", "/recargas", { instanceId: state.instanceId, ...pending }), id); }
  catch (error) {
    if ([400, 403, 404, 409].includes(error?.response?.status)) {
      const current = load(scope); delete current.pendingTopup; save(scope, current);
    }
    throw error;
  }
  const current = load(scope); delete current.pendingTopup;
  save(scope, { ...current, configured: result.configured, config: result });
  return result;
}
